import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { FormBuilder } from '@/components/forms/FormBuilder';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import type { DynamicForm } from '@/types/dynamic-form';

export default function FormEditPage() {
  const { formId } = useParams<{ formId: string }>();
  const [form, setForm] = useState<DynamicForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!formId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await dynamicFormsApi.get(formId);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to load form');
      }
      setForm(res.data.form);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load form');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {loading ? (
        <div className="mx-auto w-full max-w-4xl space-y-4 p-1">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-[480px] w-full rounded-xl" />
        </div>
      ) : error ? (
        <div className="mx-auto max-w-3xl space-y-8 p-1">
          <Button variant="ghost" size="sm" className="w-fit gap-2 px-0 text-muted-foreground" asChild>
            <Link to="/f">
              <ArrowLeft className="h-4 w-4" />
              All forms
            </Link>
          </Button>
          <Card className="border-destructive/30 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-destructive">Could not load form</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" asChild>
                <Link to="/f">Back to forms</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      ) : form ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <FormBuilder form={form} onFormUpdated={setForm} />
        </div>
      ) : null}
    </div>
  );
}
